// Auto-fix settings (README, "Auto-fix").
// Site settings → Auto-fix (owners and admins): the site's GitHub repository and branch, the mode
// (Off / Ask me / Automatic) and when an error counts as "keeps happening", plus recent attempts.
// Settings → Alerts → Auto-fix (admins): the daily cap across all sites and whether the GitHub
// token is set (never shown).
import type { AttemptView, AutofixGlobalView, AutofixMode, AutofixSettingsView } from "@monoblock/autofix/src/types";

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/${path}`, {
    method,
    credentials: "same-origin",
    headers: body ? { "Content-Type": "application/json" } : {},
    body: body ? JSON.stringify(body) : undefined,
  });
  const out = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
  if (!res.ok) throw new Error((out as { error?: string }).error ?? `HTTP ${res.status}`);
  return out as T;
}

const ERRORS: Record<string, string> = {
  bad_repo: "Enter the repository as owner/name, e.g. azeemazeez/kanzen.",
  bad_branch: "That isn't a valid branch name.",
  repo_required: "Set the repository before turning auto-fix on.",
  bad_min_events: "Events must be a whole number from 1 to 10,000.",
  bad_min_visitors: "Visitors must be a whole number from 1 to 1,000.",
  bad_window_hours: "Pick one of the listed time windows.",
  bad_min_age_hours: "Pick one of the listed ages.",
  bad_daily_cap: "The daily limit must be a whole number from 0 to 50.",
  read_only: "Only owners of this site can change auto-fix.",
  not_allowed: "Only MonoLog admins can change this.",
};
const say = (el: HTMLElement, text: string, error = false) => {
  el.textContent = text;
  el.className = error ? "acct-msg error" : "acct-msg";
};
const fmtWhen = (ms: number) => new Date(ms).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const MODES: { value: AutofixMode; title: string; desc: string }[] = [
  { value: "off", title: "Off", desc: "Nothing happens. Fix it isn't offered." },
  { value: "ask", title: "Ask me", desc: "A Ping notification with a Fix it button. Nothing runs until someone presses it." },
  { value: "auto", title: "Automatic", desc: "Sent to GitHub straight away, within the daily limit for all sites; past it, you're asked instead." },
];

const STATUS: Record<AttemptView["status"], [string, string]> = {
  suggested: ["Asked", "none"],
  requested: ["Requested", "ni"],
  dispatched: ["Working", "ni"],
  pr_opened: ["PR opened", "good"],
  issue_opened: ["Issue opened", "none"],
  no_fix: ["No fix", "none"],
  failed: ["Failed", "poor"],
};

function attemptRow(siteId: string, a: AutofixSettingsView["attempts"][number]): string {
  const [label, cls] = STATUS[a.status];
  const what = `${a.type ?? "Error"}${a.message ? `: ${a.message}` : ""}`;
  const link = a.result_url ? ` <a href="${esc(a.result_url)}" target="_blank" rel="noopener noreferrer">${a.status === "pr_opened" ? "PR" : "GitHub"}</a>` : "";
  return `<div class="alr-inc"><span class="pill ${cls}">${a.resolved_at ? "Resolved" : label}</span><a class="alr-inc-what af-what-line" href="/project/${encodeURIComponent(siteId)}/errors/?fp=${encodeURIComponent(a.fingerprint)}" title="${esc(what)}">${esc(what.slice(0, 90))}</a>${link}<span class="muted">${fmtWhen(a.finished_at ?? a.dispatched_at ?? a.requested_at ?? a.created_at)}</span></div>`;
}

interface SiteOpts {
  el: HTMLElement;
  site: { id: string; name: string };
  current: () => boolean;
}

export async function renderAutofixSettings(o: SiteOpts): Promise<void> {
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Auto-fix</h2></div><div class="acct-msg">Loading…</div></div>`;
  try {
    const v = await call<AutofixSettingsView>("GET", `autofix/settings?project=${encodeURIComponent(o.site.id)}`);
    if (o.current()) drawSite(o, v);
  } catch (err) {
    if (o.current()) o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Auto-fix</h2></div><div class="acct-msg error">Couldn't load auto-fix (${esc((err as Error).message)}).</div></div>`;
  }
}

function drawSite(o: SiteOpts, v: AutofixSettingsView): void {
  const s = v.settings;
  const opt = (n: number, label: string, cur: number) => `<option value="${n}"${n === cur ? " selected" : ""}>${label}</option>`;
  const hours = (h: number) => (h === 0 ? "any age" : h === 1 ? "1 hour" : `${h} hours`);
  const recent = v.attempts.length ? `<div class="alr-recent"><h3>Recent</h3>${v.attempts.map((a) => attemptRow(v.project, a)).join("")}</div>` : "";
  o.el.innerHTML = `<div class="card">
    <div class="card-head"><h2>Auto-fix</h2></div>
    <p class="muted usr-intro">When an error keeps happening, Claude can look into it in the site's GitHub repository (running in GitHub Actions there) and open a pull request with a fix, or an issue with what it found. Nothing is ever merged automatically. The repository needs the MonoLog workflow installed: see <span class="mono">sdk/autofix/README.md</span>.</p>
    ${v.token_configured ? "" : `<div class="err-note">MonoLog's GitHub token (GITHUB_DISPATCH_TOKEN) isn't set yet, so nothing can be sent to GitHub. A MonoLog admin sets it up.</div>`}
    <form class="alr-form af-form">
      <div class="gea-grid">
        <label class="fld"><span>GitHub repository</span><input class="acct-input mono" name="repo" placeholder="owner/name" value="${esc(s.repo ?? "")}" autocomplete="off" spellcheck="false"></label>
        <label class="fld"><span>Branch</span><input class="acct-input mono" name="branch" placeholder="main" value="${esc(s.branch)}" autocomplete="off" spellcheck="false" required></label>
      </div>
      <fieldset class="alr-rules"><legend>When an error keeps happening</legend>${MODES.map(
        (m) => `<label class="alr-rule"><input type="radio" name="mode" value="${m.value}"${m.value === s.mode ? " checked" : ""}><span><b>${m.title}</b><span class="muted">${m.desc}</span></span></label>`,
      ).join("")}</fieldset>
      <div class="gea-grid af-thresholds">
        <label class="fld"><span>At least</span><span class="gea-min"><input class="acct-input" type="number" name="min_events" inputmode="numeric" min="${v.options.min_events[0]}" max="${v.options.min_events[1]}" step="1" value="${s.min_events}" required><span class="muted">events</span></span></label>
        <label class="fld"><span>From at least</span><span class="gea-min"><input class="acct-input" type="number" name="min_visitors" inputmode="numeric" min="${v.options.min_visitors[0]}" max="${v.options.min_visitors[1]}" step="1" value="${s.min_visitors}" required><span class="muted">visitors</span></span></label>
        <label class="fld"><span>Within</span><select class="btn" name="window_hours">${v.options.window_hours.map((h) => opt(h, hours(h), s.window_hours)).join("")}</select></label>
        <label class="fld"><span>First seen at least</span><select class="btn" name="min_age_hours">${v.options.min_age_hours.map((h) => opt(h, h === 0 ? "any time" : `${hours(h)} ago`, s.min_age_hours)).join("")}</select></label>
      </div>
      <p class="muted usr-note">Only open errors with a frame in the site's own code count; ignored and third-party ones never do. Each error is suggested or sent once; Fix it on the error's page can try again.</p>
      <div class="usr-actions"><button class="btn primary" type="submit">Save auto-fix</button><span class="acct-msg" role="status"></span></div>
    </form>
    ${recent}
  </div>`;

  const form = o.el.querySelector<HTMLFormElement>(".af-form")!;
  const repo = form.querySelector<HTMLInputElement>('input[name="repo"]')!;
  // A repo typed into an "Off" site turns on "Ask me", the usual choice; it can still be changed.
  const hadRepo = !!s.repo;
  repo.addEventListener("input", () => {
    const off = form.querySelector<HTMLInputElement>('input[name="mode"][value="off"]')!;
    const askMode = form.querySelector<HTMLInputElement>('input[name="mode"][value="ask"]')!;
    if (!hadRepo && repo.value.trim() && off.checked) askMode.checked = true;
    if (!repo.value.trim()) off.checked = true;
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = form.querySelector<HTMLElement>("[role=status]")!;
    const f = new FormData(form);
    const settings = {
      repo: String(f.get("repo") ?? "").trim() || null,
      branch: String(f.get("branch") ?? "").trim(),
      mode: String(f.get("mode") ?? "off"),
      min_events: Number(f.get("min_events")),
      min_visitors: Number(f.get("min_visitors")),
      window_hours: Number(f.get("window_hours")),
      min_age_hours: Number(f.get("min_age_hours")),
    };
    try {
      const saved = await call<AutofixSettingsView>("PUT", "autofix/settings", { project: v.project, settings });
      if (!o.current()) return;
      drawSite(o, saved);
      say(o.el.querySelector<HTMLElement>(".af-form [role=status]")!, "Saved.");
    } catch (err) {
      say(msg, ERRORS[(err as Error).message] ?? `Couldn't save (${(err as Error).message}).`, true);
    }
  });
}

interface GlobalOpts {
  el: HTMLElement;
  current: () => boolean;
}

export async function renderAutofixGlobal(o: GlobalOpts): Promise<void> {
  o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Auto-fix</h2></div><div class="acct-msg">Loading…</div></div>`;
  try {
    const v = await call<AutofixGlobalView>("GET", "autofix/global");
    if (o.current()) drawGlobal(o, v);
  } catch (err) {
    if (o.current()) o.el.innerHTML = `<div class="card"><div class="card-head"><h2>Auto-fix</h2></div><div class="acct-msg error">Couldn't load auto-fix (${esc((err as Error).message)}).</div></div>`;
  }
}

const MODE_LABEL: Record<AutofixMode, string> = { off: "Off", ask: "Ask me", auto: "Automatic" };

function drawGlobal(o: GlobalOpts, v: AutofixGlobalView): void {
  const sites = v.sites.length
    ? v.sites
        .map(
          (s) =>
            `<div class="alr-inc"><span class="pill ${s.mode === "off" ? "none" : s.mode === "auto" ? "ni" : "good"}">${MODE_LABEL[s.mode]}</span><span class="alr-inc-what">${esc(s.name)}</span><span class="mono muted">${esc(s.repo ?? "")}</span></div>`,
        )
        .join("")
    : `<p class="muted usr-note">No site has a repository yet: set one in Site settings → Auto-fix.</p>`;
  o.el.innerHTML = `<div class="card">
    <div class="card-head"><h2>Auto-fix</h2></div>
    <div class="af-token ${v.token_configured ? "good" : ""} err-note">${
      v.token_configured
        ? "GitHub token: set (GITHUB_DISPATCH_TOKEN). It's never shown."
        : "GitHub token: not set. Add a fine-grained token with Contents: read and write on the sites' repositories as the GITHUB_DISPATCH_TOKEN secret on the dashboard and processor (README, “Auto-fix”)."
    }</div>
    <form class="alr-form afg-form">
      <div class="gea-grid">
        <label class="fld"><span>Automatic fixes per day, all sites</span><span class="gea-min"><input class="acct-input" type="number" name="daily_cap" inputmode="numeric" min="0" max="50" step="1" value="${v.daily_cap}" required><span class="muted">${v.used_24h} sent in the last 24 hours</span></span></label>
      </div>
      <p class="muted usr-note">Every fix sent to GitHub counts, Fix it included; only Automatic waits for the limit (those are asked in Ping instead). 0 means Automatic always asks.</p>
      <div class="usr-actions"><button class="btn primary" type="submit">Save</button><span class="acct-msg" role="status"></span></div>
    </form>
    <div class="alr-recent"><h3>Sites</h3>${sites}</div>
  </div>`;
  const form = o.el.querySelector<HTMLFormElement>(".afg-form")!;
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    const msg = form.querySelector<HTMLElement>("[role=status]")!;
    try {
      const saved = await call<AutofixGlobalView>("PUT", "autofix/global", { daily_cap: Number(new FormData(form).get("daily_cap")) });
      if (!o.current()) return;
      drawGlobal(o, saved);
      say(o.el.querySelector<HTMLElement>(".afg-form [role=status]")!, "Saved.");
    } catch (err) {
      say(msg, ERRORS[(err as Error).message] ?? `Couldn't save (${(err as Error).message}).`, true);
    }
  });
}
